import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

import {
  Box,
  Chip,
  IconButton,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import { PageWrapper } from '../../components';
import ForecastModal from './ForecastModal';
import {
  getAccountById,
  getLoading,
  getQuarterlyRevenueSortOrder,
  getSortQuarterlyRevenueBy,
} from '../../redux/quarterlyRevenue/quarterlyRevenue.selectors';
import {
  GrowthSegmentationLabels,
  Mode,
  QuarterlyRevenueAccountSummary,
} from '../../types/quarterlyRevenue';
import {
  agileEngineRevenueLabels,
  clientGrowthLabels,
  RevenueStatusChip,
} from '../../components/revenueStatusChip/RevenueStatusChip';
import { useParams } from 'react-router-dom';
import {
  SortQuarterlyRevenueBy,
  getQuarterlyRevenueById,
  quarterlyRevenueSlice,
} from '../../redux/quarterlyRevenue';
import { getSelectedAccount } from '../../redux/account';
import { Account, SortOrder } from '../../types';
import EditIcon from '@mui/icons-material/Edit';
import { DateTime } from 'luxon';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { COLORS } from '../../utils/colors';
import { useTitle } from '../../hooks/useTitle';
import { PageTitle } from '../../utils/pageTitles';

export const AccountRevenues: React.FC = () => {
  const { accountId } = useParams();
  const account = useAppSelector(getSelectedAccount);
  const dispatch = useAppDispatch();
  const quarterlyRevenueAccounts = useAppSelector(getAccountById);
  const loading = useAppSelector(getLoading);

  const [isQuarterlyRevenueModalOpen, setQuarterlyRevenueModalOpen] =
    useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedRevenueStatus, setSelectedRevenueStatus] = useState<
    QuarterlyRevenueAccountSummary | undefined
  >(undefined);

  const [mode, setMode] = useState<Mode>(Mode.VIEW);

  const setPageTitle = useTitle();

  useEffect(() => {
    if (accountId) dispatch(getQuarterlyRevenueById(accountId));
  }, [accountId, dispatch]);

  useEffect(() => {
    setPageTitle(PageTitle.forecast);
  }, [setPageTitle]);

  // A stub row (nightly sync, no forecast yet) has every real forecast
  // field left null — only `overview` being set means a human filled it in.
  const currentQuarterStub = useMemo(
    () =>
      quarterlyRevenueAccounts.find(
        (revenue) =>
          revenue.quarter.toString() === DateTime.now().toFormat('yyyyqq') &&
          revenue.overview == null,
      ),
    [quarterlyRevenueAccounts],
  );

  const hasCurrentQuarterSummary = useMemo(
    () =>
      quarterlyRevenueAccounts.find(
        (revenue) =>
          revenue.quarter.toString() === DateTime.now().toFormat('yyyyqq') &&
          revenue.overview != null,
      ),
    [quarterlyRevenueAccounts],
  );

  const filledQuarterlyRevenueAccounts = useMemo(
    () =>
      quarterlyRevenueAccounts.filter((revenue) => revenue.overview != null),
    [quarterlyRevenueAccounts],
  );

  const onEditRevenueSummary = useCallback(
    (data: QuarterlyRevenueAccountSummary) => {
      if (data.accountId) {
        setSelectedAccount(data.accountId);
      }
      setMode(Mode.EDIT);
      setSelectedRevenueStatus(data);
      setQuarterlyRevenueModalOpen((prev) => !prev);
    },
    [],
  );

  const onAddRevenueSummary = useCallback(() => {
    setSelectedAccount(accountId ?? '');
    // A stub row (nightly sync, no forecast yet) isn't shown as a normal
    // table row, but its previousQuarterRevenue is still real — pass it
    // through so the modal can pre-fill Actuals for the new summary.
    setSelectedRevenueStatus(currentQuarterStub);
    setMode(Mode.EDIT);
    setQuarterlyRevenueModalOpen((prev) => !prev);
  }, [accountId, currentQuarterStub]);

  const onViewRevenueSummary = useCallback(
    (data: QuarterlyRevenueAccountSummary) => {
      if (data.accountId) {
        setSelectedAccount(data.accountId);
      }
      setMode(Mode.VIEW);
      setSelectedRevenueStatus(data);
      setQuarterlyRevenueModalOpen((prev) => !prev);
    },
    [],
  );

  return (
    <PageWrapper
      isLoading={loading}
      sx={{
        pt: 0,
        pb: 0,
      }}
    >
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}
      >
        <Table aria-label="collapsible table" stickyHeader>
          <AccountRevenuesTableHeader />
          <TableBody>
            {!hasCurrentQuarterSummary && (
              <TableRow
                sx={{
                  cursor: 'pointer',
                  transition: 'background 0.25s',
                  backgroundColor: 'highlight.neutral',
                  ':hover': { backgroundColor: 'action.hover' },
                }}
                onClick={onAddRevenueSummary}
              >
                <CustomTableCell>
                  <Tooltip
                    title="Add quarterly revenue summary"
                    placement="top-end"
                  >
                    <Typography>
                      <IconButton size="small" sx={{ color: 'primary.main' }}>
                        <AddCommentIcon fontSize="small" />
                      </IconButton>
                      {'Q' + DateTime.now().toFormat('q')}
                    </Typography>
                  </Tooltip>
                </CustomTableCell>
                <TableCell colSpan={10}>
                  <Typography sx={{ color: COLORS.ACTION.PRIMARY }}>
                    Add revenue summary
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filledQuarterlyRevenueAccounts.map(
              (row: QuarterlyRevenueAccountSummary, i: number) => (
                <AccountRowWithStatus
                  key={i}
                  row={row}
                  account={account}
                  onEditRevenueSummary={onEditRevenueSummary}
                  onViewRevenueSummary={onViewRevenueSummary}
                />
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ForecastModal
        isModalOpen={isQuarterlyRevenueModalOpen}
        handleCloseModal={() => setQuarterlyRevenueModalOpen(false)}
        selectedAccount={selectedAccount}
        data={selectedRevenueStatus}
        selectedQuarter={
          selectedRevenueStatus?.quarter.toString() ??
          DateTime.now().toFormat('yyyyqq')
        }
        mode={mode}
        setMode={setMode}
      />
    </PageWrapper>
  );
};

const AccountRevenuesTableHeader: React.FC = () => {
  const sortBy = useAppSelector(getSortQuarterlyRevenueBy);
  const sortOrder = useAppSelector(getQuarterlyRevenueSortOrder);
  const dispatch = useAppDispatch();

  const handleSort = useCallback(
    (column: string) => {
      const sortMap: Record<string, SortQuarterlyRevenueBy> = {
        Quarter: SortQuarterlyRevenueBy.Quarter,
        RevenueStatus: SortQuarterlyRevenueBy.RevenueStatus,
        ClientGrowth: SortQuarterlyRevenueBy.ClientGrowth,
        Headcount: SortQuarterlyRevenueBy.Headcount,
        AeRevenueDecreaseReasons:
          SortQuarterlyRevenueBy.AeRevenueDecreaseReasons,
      };
      const newSortBy = sortMap[column];
      if (!newSortBy) return;

      const newSortOrder =
        newSortBy === sortBy ? 1 - sortOrder : SortOrder.Ascending;
      dispatch(
        quarterlyRevenueSlice.actions.sortRevenuesBy({
          sortBy: newSortBy,
          sortOrder: newSortOrder,
        }),
      );
    },
    [dispatch, sortOrder, sortBy],
  );
  return (
    <TableHead>
      <TableRow
        sx={{
          '& .MuiTableCell-root': {
            padding: '12px 16px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <TableCell>
          <TableSortLabel
            direction={
              sortBy === SortQuarterlyRevenueBy.Quarter
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortQuarterlyRevenueBy.Quarter}
            onClick={() => handleSort('Quarter')}
          >
            <Typography variant="body2" fontWeight={500}>
              Quarter
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell>
          <TableSortLabel
            direction={
              sortBy === SortQuarterlyRevenueBy.RevenueStatus
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortQuarterlyRevenueBy.RevenueStatus}
            onClick={() => handleSort('RevenueStatus')}
          >
            <Typography variant="body2" fontWeight={500}>
              Revenue forecast
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell>
          <TableSortLabel
            direction={
              sortBy === SortQuarterlyRevenueBy.Headcount
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortQuarterlyRevenueBy.Headcount}
            onClick={() => handleSort('Headcount')}
          >
            <Typography whiteSpace="nowrap" variant="body2" fontWeight={500}>
              HC delta
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell>
          <TableSortLabel
            direction={
              sortBy === SortQuarterlyRevenueBy.AeRevenueDecreaseReasons
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortQuarterlyRevenueBy.AeRevenueDecreaseReasons}
            onClick={() => handleSort('AeRevenueDecreaseReasons')}
          >
            <Typography variant="body2" fontWeight={500}>
              AE revenue decrease reasons
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell>
          <TableSortLabel
            direction={
              sortBy === SortQuarterlyRevenueBy.ClientGrowth
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortQuarterlyRevenueBy.ClientGrowth}
            onClick={() => handleSort('ClientGrowth')}
          >
            <Typography variant="body2" fontWeight={500}>
              Client growth
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell>
          <Typography whiteSpace="nowrap" variant="body2" fontWeight={500}>
            Expected
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography whiteSpace="nowrap" variant="body2" fontWeight={500}>
            PDM
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            whiteSpace="nowrap"
            variant="body2"
            fontWeight={500}
          ></Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

interface AccountRowWithStatusProps {
  row: QuarterlyRevenueAccountSummary;
  account: Account | null;
  onEditRevenueSummary: (data: QuarterlyRevenueAccountSummary) => void;
  onViewRevenueSummary: (data: QuarterlyRevenueAccountSummary) => void;
}

const AccountRowWithStatus: React.FC<AccountRowWithStatusProps> = ({
  row,
  account,
  onEditRevenueSummary,
  onViewRevenueSummary,
}) => {
  const renderChip = useCallback(
    (item: string, index: number) => (
      <Chip
        key={index}
        label={item}
        size="small"
        sx={{
          fontSize: '0.75rem',
          color: 'text.primary',
        }}
      />
    ),
    [],
  );

  return (
    <>
      <TableRow
        sx={{
          cursor: 'pointer',
          transition: 'background 0.25s',
          verticalAlign: 'baseline',
          '&:hover': {
            background: (theme) => theme.palette.action.hover,
          },
          '& .MuiTableCell-root': {
            padding: '8px 16px',
          },
          height: '100% !important',
        }}
        hover
        onClick={() => onViewRevenueSummary(row)}
      >
        <CustomTableCell sx={{ paddingLeft: '8px !important' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1.25,
              ml: '10px',
            }}
          >
            <DoneRoundedIcon
              sx={{
                color: 'text.secondary',
                fontSize: '16px',
                alignSelf: 'anchor-center',
              }}
            />
            <Typography
              whiteSpace="nowrap"
              variant="h6"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-around"
              >
                {`Q${row.quarter % 100}`}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 1.25 }}
                >
                  {Math.floor(row.quarter / 100)}
                </Typography>
              </Box>
            </Typography>
          </Box>
        </CustomTableCell>
        <CustomTableCell>
          <RevenueStatusChip
            value={row.agileEngineRevenue}
            label={
              agileEngineRevenueLabels[
                row.agileEngineRevenue as keyof typeof agileEngineRevenueLabels
              ]
            }
            type="agileEngineRevenue"
          />
        </CustomTableCell>
        <CustomTableCell>
          <Box display="flex" alignItems="center" justifyContent="start">
            {Number(row.headCount)}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.25 }}
            >
              {row.hcDelta !== 0 && row.hcDelta > 0
                ? `(+${Number(row.hcDelta).toFixed(1)})`
                : `(${Number(row.hcDelta).toFixed(1)})`}
            </Typography>
          </Box>
        </CustomTableCell>
        <CustomTableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Array.isArray(row.aeRevenueDecreaseReasons) &&
              row.aeRevenueDecreaseReasons.map(renderChip)}
          </Box>
        </CustomTableCell>
        <CustomTableCell>
          <RevenueStatusChip
            value={row.clientGrowth}
            label={
              clientGrowthLabels[
                row.clientGrowth as keyof typeof clientGrowthLabels
              ]
            }
            type="clientGrowth"
          />
        </CustomTableCell>
        <CustomTableCell>
          <Typography
            variant="body2"
            color="text.secondary"
            whiteSpace="nowrap"
          >
            {row.growthSegmentationValue && row.growthSegmentationValue + '%'}
            {row.growthSegmentation ? (
              <Chip
                label={
                  GrowthSegmentationLabels[
                    row.growthSegmentation as keyof typeof GrowthSegmentationLabels
                  ]
                }
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.primary',
                  marginLeft: row.growthSegmentationValue ? 1 : 0,
                }}
              />
            ) : (
              <Box
                component="span"
                sx={{
                  color: 'text.disabled',
                  mx: 2,
                  pr: 2.5,
                }}
              >
                &mdash;
              </Box>
            )}
          </Typography>
        </CustomTableCell>
        <CustomTableCell>
          {account?.portfolioDeliveryManager ? (
            <Tooltip
              title={account?.portfolioDeliveryManager.fullName || ''}
              placement="top"
            >
              <Typography
                whiteSpace="nowrap"
                variant="body1"
                textOverflow={'ellipsis'}
              >
                {account.portfolioDeliveryManager.fullName}
              </Typography>
            </Tooltip>
          ) : (
            <Typography whiteSpace="nowrap" variant="body2">
              Not assigned
            </Typography>
          )}
        </CustomTableCell>
        <CustomTableCell>
          <Tooltip title="Edit" placement="top">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onEditRevenueSummary(row);
              }}
            >
              <EditIcon sx={{ fontSize: '19px', color: 'primary.main' }} />
            </IconButton>
          </Tooltip>
        </CustomTableCell>
      </TableRow>
    </>
  );
};

interface CustomTableCellProps {
  sx?: SxProps<Theme> | undefined;
  customProps?: TableCellProps;
}

const CustomTableCell: React.FC<PropsWithChildren<CustomTableCellProps>> = ({
  children,
  sx,
  customProps,
}) => {
  return (
    <TableCell
      sx={{
        padding: '12px 16px',
        ...sx,
      }}
      {...customProps}
    >
      {children}
    </TableCell>
  );
};
